import { observer } from 'mobx-react'
import React from 'react'
import LayoutStore from '../../State/LayoutStore'
import Table from '../../Components/Table'
import BillingPageStore from './BillingPageStore'
import SessionStore from '../../State/SessionStore'
import { FormatCurrency } from '../../Utils/Localisation/CurrencyFormatter'
import { FormatPercent } from '../../Utils/Localisation/PercentFormatter'
import { FormatDate } from '../../Utils/Localisation/DateFormatter'
import { makeRequest } from '../../Queries/makeRequest'
import BillingEmailModal from './BillingEmailModal'
import StaffCollection from '../../State/Collections/StaffCollection'
import * as Sentry from '@sentry/react'
import LoadingSpinner from '../../Components/LoadingSpinner'
import MiniLoadingSpinner from '../../Components/MiniLoadingSpinner'
import posthog from 'posthog-js'
import { trpc } from '@/system/trpc'
import { CreditCard } from 'lucide-react'
import { Link } from '@tanstack/react-router'

const unpaidInvoiceColumns = [
    {
        id: 'label',
        label: 'Unpaid Invoices',
        type: 'text',
        width: 20,
        value: (row) => {
            return row.name
        },
    },
    {
        id: 'date',
        label: '',
        type: 'date',
        width: 15,
        value: (row) => {
            return new Date(row.date)
        },
    },
    {
        id: 'amount',
        label: '',
        type: 'currency',
        width: 25,
        value: (row) => {
            return row.amount
        },
    },
    {
        id: 'payButton',
        label: '',
        type: 'text',
        width: 10,
        value: (row) => {
            return 'pay...'
        },
    },
]

const paymentDetailColumns = [
    {
        id: 'label',
        label: 'Payment Details',
        type: 'text',
        width: 20,
        value: (row) => {
            return row.label
        },
    },
    {
        id: 'value',
        label: '',
        type: 'text',
        width: 50,
        value: (row) => {
            return row.value
        },
        component: ({ value, stores }) => {
            return value
        },
    },
]

const nextPaymentColumns = [
    {
        id: 'label',
        label: 'Next Payment',
        type: 'text',
        width: 20,
        style: (row) => ({
            fontWeight: row?.label === 'Total' ? 'bold' : 'normal',
        }),
        value: (row) => {
            return row.label
        },
    },
    {
        id: 'percent',
        label: '',
        type: 'text',
        width: 25,
        style: (row) => ({
            fontWeight: row?.label === 'Total' ? 'bold' : 'normal',
        }),
        value: (row) => {
            return row.percent
        },
        component: ({ value, stores }) => {
            const row = stores.row.rowObject

            // Show "Remaining Credit" for total row when grand total is negative
            if (row.isTotal && row.amount && row.amount < 0) {
                return (
                    <span
                        style={{
                            textAlign: 'right',
                            fontStyle: 'italic',
                            fontWeight: 400,
                            width: '100%',
                        }}
                    >
                        Remaining Credit
                    </span>
                )
            }

            return value || ''
        },
    },
    {
        id: 'amount',
        label: '',
        type: 'currency',
        format: (v) => FormatCurrency(v),
        width: 25,
        style: (row) => ({
            fontWeight: row?.label === 'Total' ? 'bold' : 'normal',
        }),
        value: (row) => {
            return row.amount
        },
        component: ({ value, formattedValue, stores }) => {
            return value !== null ? formattedValue : ''
        },
    },
]

const createStaffColumns = () => [
    {
        id: 'label',
        label: 'Staff Breakdown',
        type: 'text',
        width: 20,
        style: (row) => ({
            opacity: row?.isBillable === false ? 0.45 : 1,
            color: row?.isBillable === false ? '#adb5bd' : 'inherit',
        }),
        value: (row) => {
            return `${row.index}. ${row.name}`
        },
    },
    {
        id: 'level',
        label: '',
        type: 'text',
        width: 40,
        style: (row) => ({
            opacity: row?.isBillable === false ? 0.45 : 1,
            color: row?.isBillable === false ? '#adb5bd' : 'inherit',
        }),
        value: (row) => {
            return row.permissionLevel
        },
        component: ({ value, stores }) => {
            const row = stores.row.rowObject
            const staffId = findStaffIdByName(row.name)

            return (
                <div
                    style={{
                        display: 'flex',
                        width: '100%',
                        textAlign: 'right',
                        opacity: row?.isBillable === false ? 0.45 : 1,
                    }}
                >
                    <div
                        style={{
                            flex: '1 1 auto',
                            padding: '0 1em',
                            color:
                                row?.isBillable === false
                                    ? '#adb5bd'
                                    : 'inherit',
                        }}
                    >
                        {value}
                    </div>
                    {staffId ? (
                        <Link
                            to="/staff/$id"
                            params={{ id: staffId }}
                            search={{ tab: 'permissions' }}
                            style={{
                                borderLeft: 'solid 1px #eee',
                                padding: '0 1em',
                                flex: '0 0 auto',
                                textDecoration: 'none',
                                color:
                                    row?.isBillable === false
                                        ? '#adb5bd'
                                        : 'inherit',
                            }}
                        >
                            edit...
                        </Link>
                    ) : (
                        <div
                            style={{
                                borderLeft: 'solid 1px #eee',
                                padding: '0 1em',
                                flex: '0 0 auto',
                                color: '#ccc',
                                cursor: 'not-allowed',
                            }}
                        >
                            edit...
                        </div>
                    )}
                </div>
            )
        },
    },
    {
        id: 'fee',
        label: '',
        type: 'currency',
        width: 10,
        format: (v) => FormatCurrency(v),
        style: (row) => ({
            opacity: row?.isBillable === false ? 0.45 : 1,
            color: row?.isBillable === false ? '#adb5bd' : 'inherit',
        }),
        value: (row) => {
            return row.fee
        },
        component: ({ value, formattedValue, stores }) => {
            return value !== null ? formattedValue : ''
        },
    },
]

// Mobile responsive utilities
const isMobile = () => window.innerWidth <= 768
const useResponsiveStyles = () => {
    const [isMobileView, setIsMobileView] = React.useState(isMobile())

    React.useEffect(() => {
        const handleResize = () => setIsMobileView(isMobile())
        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [])

    return isMobileView
}

// Helper function to find staff ID by name
const findStaffIdByName = (staffName) => {
    if (!staffName || !StaffCollection.staff) return null

    // Find staff member by matching the name
    const staff = StaffCollection.staff.find((s) => {
        const fullName = `${s.firstName || ''} ${s.lastName || ''}`.trim()
        return fullName === staffName.trim()
    })

    return staff?.id || null
}

// Helper function to find full staff object by name
const findStaffByName = (staffName) => {
    if (!staffName || !StaffCollection.staff) return null

    // Find staff member by matching the name
    const staff = StaffCollection.staff.find((s) => {
        const fullName = `${s.firstName || ''} ${s.lastName || ''}`.trim()
        return fullName === staffName.trim()
    })

    return staff || null
}

// Billing Period Toggle Component
const BillingPeriodToggle = observer(() => {
    const currentPeriod =
        BillingPageStore.billingPeriod ||
        SessionStore.subscription?.subscriptionPeriod ||
        'monthly'
    const hasSubscription = SessionStore.subscription?.hasSubscription
    const isMobileView = useResponsiveStyles()

    const handlePeriodChange = (newPeriod) => {
        BillingPageStore.setBillingPeriod(newPeriod)
        // Trigger preview calculation when period changes
        BillingPageStore.calculatePreview()
    }

    const getPreviewMessage = () => {
        if (!BillingPageStore.billingPeriod) return null

        if (!hasSubscription) {
            // New subscription scenario
            const savings =
                BillingPageStore.billingPeriod === 'yearly' ? ' (Save 15%)' : ''

            let totalText = ''
            if (BillingPageStore.subscriptionPreview) {
                const grandTotal = BillingPageStore.previewComputedGrandTotal
                const preview = BillingPageStore.subscriptionPreview

                if (
                    grandTotal === 0 &&
                    preview.credit &&
                    Math.abs(preview.credit) > 0
                ) {
                    // Credit covers the entire amount - show remaining credit to match table total
                    const remainingCredit = preview.remainingCredit || 0
                    if (remainingCredit > 0) {
                        totalText = ` - Remaining Credit: ${FormatCurrency(remainingCredit)}`
                    } else {
                        totalText = ` - Credit Applied: ${FormatCurrency(Math.abs(preview.credit))}`
                    }
                } else {
                    totalText = ` - Total: ${FormatCurrency(grandTotal)}`
                }
            }

            return {
                icon: '💰',
                text: `${BillingPageStore.billingPeriod} subscription preview${savings}${totalText}`,
                type: 'info',
                buttonText: 'Subscribe Now',
            }
        } else if (
            BillingPageStore.billingPeriod !==
            SessionStore.subscription.subscriptionPeriod
        ) {
            // Billing period change scenario
            const fromPeriod = SessionStore.subscription.subscriptionPeriod
            const toPeriod = BillingPageStore.billingPeriod
            const isUpgrade = toPeriod === 'yearly'

            let totalText = ''
            if (BillingPageStore.subscriptionPreview) {
                const grandTotal = BillingPageStore.previewComputedGrandTotal
                const preview = BillingPageStore.subscriptionPreview

                if (
                    grandTotal === 0 &&
                    preview.credit &&
                    Math.abs(preview.credit) > 0
                ) {
                    // Credit covers the entire amount - show remaining credit to match table total
                    const remainingCredit = preview.remainingCredit || 0
                    if (remainingCredit > 0) {
                        totalText = ` - Remaining Credit: ${FormatCurrency(remainingCredit)}`
                    } else {
                        totalText = ` - Credit Applied: ${FormatCurrency(Math.abs(preview.credit))}`
                    }
                } else {
                    totalText = ` - New total: ${FormatCurrency(grandTotal)}`
                }
            }

            return {
                icon: isUpgrade ? '📈' : '📉',
                text: `Switch from ${fromPeriod} to ${toPeriod} billing${totalText}`,
                type: isUpgrade ? 'upgrade' : 'downgrade',
                buttonText: 'Confirm Change',
                subtitle: isUpgrade
                    ? "You'll be charged a prorated amount and save on future payments"
                    : "You'll receive credit for unused time on your current plan",
            }
        }

        return null
    }

    const previewMessage = getPreviewMessage()

    return (
        <div
            style={{
                margin: '2em 0',
                padding: isMobileView ? '1em' : '1.5em',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                border: '1px solid #e9ecef',
            }}
        >
            <div
                style={{
                    display: 'flex',
                    alignItems: isMobileView ? 'flex-start' : 'center',
                    justifyContent: 'space-between',
                    flexDirection: isMobileView ? 'column' : 'row',
                    marginBottom: hasSubscription ? '1em' : '0.5em',
                    gap: isMobileView ? '1em' : '0',
                }}
            >
                <div style={{ width: isMobileView ? '100%' : 'auto' }}>
                    <h3
                        style={{
                            margin: 0,
                            fontSize: isMobileView ? '1em' : '1.1em',
                            fontWeight: '600',
                        }}
                    >
                        {hasSubscription
                            ? 'Change Billing Period'
                            : 'Choose Billing Period'}
                    </h3>
                    {!hasSubscription && (
                        <p
                            style={{
                                margin: '0.5em 0 0 0',
                                fontSize: isMobileView ? '0.8em' : '0.85em',
                                color: '#6c757d',
                                fontStyle: 'italic',
                            }}
                        >
                            Select your preferred billing cycle and see the cost
                            breakdown
                        </p>
                    )}
                </div>

                <div
                    style={{
                        display: 'flex',
                        backgroundColor: 'white',
                        border: '1px solid #dee2e6',
                        borderRadius: '6px',
                        overflow: 'hidden',
                        width: isMobileView ? '100%' : 'auto',
                    }}
                >
                    <button
                        style={{
                            padding: isMobileView ? '0.75em 1em' : '0.5em 1em',
                            border: 'none',
                            backgroundColor:
                                currentPeriod === 'monthly'
                                    ? '#007bff'
                                    : 'white',
                            color:
                                currentPeriod === 'monthly'
                                    ? 'white'
                                    : '#495057',
                            cursor: BillingPageStore.isFetchingPreview
                                ? 'not-allowed'
                                : 'pointer',
                            fontSize: isMobileView ? '0.9em' : '0.9em',
                            transition: 'all 0.2s ease',
                            opacity: BillingPageStore.isFetchingPreview
                                ? 0.6
                                : 1,
                            flex: isMobileView ? '1' : 'none',
                            whiteSpace: 'nowrap',
                        }}
                        onClick={() => handlePeriodChange('monthly')}
                        aria-pressed={currentPeriod === 'monthly'}
                        disabled={BillingPageStore.isFetchingPreview}
                    >
                        {BillingPageStore.isFetchingPreview &&
                        BillingPageStore.billingPeriod === 'monthly' ? (
                            <span
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <MiniLoadingSpinner />
                                <span style={{ marginLeft: '0.5em' }}>
                                    {isMobileView ? 'Monthly' : 'Monthly'}
                                </span>
                            </span>
                        ) : (
                            'Monthly'
                        )}
                    </button>
                    <button
                        style={{
                            padding: isMobileView ? '0.75em 1em' : '0.5em 1em',
                            border: 'none',
                            borderLeft: '1px solid #dee2e6',
                            backgroundColor:
                                currentPeriod === 'yearly'
                                    ? '#007bff'
                                    : 'white',
                            color:
                                currentPeriod === 'yearly'
                                    ? 'white'
                                    : '#495057',
                            cursor: BillingPageStore.isFetchingPreview
                                ? 'not-allowed'
                                : 'pointer',
                            fontSize: isMobileView ? '0.9em' : '0.9em',
                            transition: 'all 0.2s ease',
                            opacity: BillingPageStore.isFetchingPreview
                                ? 0.6
                                : 1,
                            flex: isMobileView ? '1' : 'none',
                            whiteSpace: 'nowrap',
                        }}
                        onClick={() => handlePeriodChange('yearly')}
                        aria-pressed={currentPeriod === 'yearly'}
                        disabled={BillingPageStore.isFetchingPreview}
                    >
                        {BillingPageStore.isFetchingPreview &&
                        BillingPageStore.billingPeriod === 'yearly' ? (
                            <span
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <MiniLoadingSpinner />
                                <span style={{ marginLeft: '0.5em' }}>
                                    {isMobileView
                                        ? 'Yearly'
                                        : 'Yearly (Save 15%)'}
                                </span>
                            </span>
                        ) : isMobileView ? (
                            'Yearly (15%)'
                        ) : (
                            'Yearly (Save 15%)'
                        )}
                    </button>
                </div>
            </div>

            {/* Loading state for preview calculation */}
            {BillingPageStore.isFetchingPreview && (
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: isMobileView ? '0.75em' : '1em',
                        backgroundColor: '#e3f2fd',
                        border: '1px solid #bbdefb',
                        borderRadius: '4px',
                        fontSize: isMobileView ? '0.85em' : '0.9em',
                        color: '#1976d2',
                    }}
                >
                    <MiniLoadingSpinner />
                    <span style={{ marginLeft: '0.5em' }}>
                        {isMobileView
                            ? 'Calculating...'
                            : 'Calculating preview...'}
                    </span>
                </div>
            )}

            {/* Error state for preview calculation */}
            {BillingPageStore.previewError &&
                !BillingPageStore.isFetchingPreview && (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: isMobileView ? '0.75em' : '1em',
                            backgroundColor: '#ffebee',
                            border: '1px solid #ffcdd2',
                            borderRadius: '4px',
                            fontSize: isMobileView ? '0.85em' : '0.9em',
                            color: '#c62828',
                        }}
                    >
                        <span style={{ marginRight: '0.5em' }}>⚠️</span>
                        {BillingPageStore.previewError}
                    </div>
                )}

            {/* Preview message and confirmation */}
            {previewMessage && !BillingPageStore.isFetchingPreview && (
                <div
                    style={{
                        padding: isMobileView ? '0.75em' : '0.75em',
                        backgroundColor:
                            previewMessage.type === 'upgrade'
                                ? '#e8f5e8'
                                : previewMessage.type === 'downgrade'
                                  ? '#fff3cd'
                                  : '#e3f2fd',
                        border: `1px solid ${
                            previewMessage.type === 'upgrade'
                                ? '#c3e6c3'
                                : previewMessage.type === 'downgrade'
                                  ? '#ffeaa7'
                                  : '#bbdefb'
                        }`,
                        borderRadius: '4px',
                        fontSize: isMobileView ? '0.85em' : '0.9em',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            alignItems: isMobileView ? 'flex-start' : 'center',
                            justifyContent: 'space-between',
                            flexDirection: isMobileView ? 'column' : 'row',
                            marginBottom: previewMessage.subtitle
                                ? '0.5em'
                                : '0',
                            gap: isMobileView ? '0.75em' : '0',
                        }}
                    >
                        <span
                            style={{
                                wordBreak: isMobileView
                                    ? 'break-word'
                                    : 'normal',
                                lineHeight: '1.4',
                            }}
                        >
                            <span style={{ marginRight: '0.5em' }}>
                                {previewMessage.icon}
                            </span>
                            {previewMessage.text}
                        </span>

                        <button
                            style={{
                                padding: isMobileView
                                    ? '0.6em 1em'
                                    : '0.4em 0.8em',
                                backgroundColor:
                                    previewMessage.type === 'upgrade'
                                        ? '#28a745'
                                        : '#007bff',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: BillingPageStore.isUpdatingSubscription
                                    ? 'not-allowed'
                                    : 'pointer',
                                fontSize: isMobileView ? '0.9em' : '0.85em',
                                fontWeight: '500',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                opacity: BillingPageStore.isUpdatingSubscription
                                    ? 0.7
                                    : 1,
                                width: isMobileView ? '100%' : 'auto',
                                minWidth: isMobileView ? 'auto' : '120px',
                            }}
                            onClick={() =>
                                hasSubscription
                                    ? BillingPageStore.confirmBillingPeriodChange()
                                    : BillingPageStore.startNewSubscription()
                            }
                            disabled={BillingPageStore.isUpdatingSubscription}
                        >
                            {BillingPageStore.isUpdatingSubscription ? (
                                <>
                                    <MiniLoadingSpinner />
                                    <span style={{ marginLeft: '0.4em' }}>
                                        {hasSubscription
                                            ? 'Updating...'
                                            : 'Processing...'}
                                    </span>
                                </>
                            ) : (
                                previewMessage.buttonText
                            )}
                        </button>
                    </div>

                    {previewMessage.subtitle && (
                        <div
                            style={{
                                fontSize: isMobileView ? '0.75em' : '0.8em',
                                color: '#6c757d',
                                fontStyle: 'italic',
                                marginTop: '0.5em',
                                lineHeight: '1.3',
                            }}
                        >
                            {previewMessage.subtitle}
                        </div>
                    )}
                </div>
            )}
        </div>
    )
})

// Preview Payment Table Columns (reusing existing styling)
const previewPaymentColumns = [
    {
        id: 'label',
        label: 'Preview - Next Payment',
        type: 'text',
        width: 20,
        style: (row) => ({
            fontWeight: row?.label === 'Total' ? 'bold' : 'normal',
            fontStyle: 'italic',
            color: '#6c757d',
        }),
        value: (row) => {
            return row.label
        },
    },
    {
        id: 'percent',
        label: '',
        type: 'text',
        width: 25,
        style: (row) => ({
            fontWeight: row?.label === 'Total' ? 'bold' : 'normal',
            fontStyle: 'italic',
            color: '#6c757d',
        }),
        value: (row) => {
            return row.percent
        },
        component: ({ value, stores }) => {
            const row = stores.row.rowObject

            // Show "Remaining Credit" for total row when grand total is negative
            if (row.isTotal && row.amount && row.amount < 0) {
                return (
                    <span
                        style={{
                            textAlign: 'right',
                            fontStyle: 'italic',
                            fontWeight: 400,
                            width: '100%',
                        }}
                    >
                        Remaining Credit
                    </span>
                )
            }

            return value || ''
        },
    },
    {
        id: 'amount',
        label: '',
        type: 'currency',
        format: (v) => FormatCurrency(v),
        width: 25,
        style: (row) => ({
            fontWeight: row?.label === 'Total' ? 'bold' : 'normal',
            fontStyle: 'italic',
            color: '#6c757d',
        }),
        value: (row) => {
            return row.amount
        },
        component: ({ value, formattedValue, stores }) => {
            return value !== null ? formattedValue : ''
        },
    },
]

// Preview Staff Breakdown Columns (reusing existing styling)
const createPreviewStaffColumns = () => [
    {
        id: 'label',
        label: 'Preview - Staff Breakdown',
        type: 'text',
        width: 20,
        style: (row) => ({
            opacity: row?.isBillable === false ? 0.45 : 1,
            fontStyle: 'italic',
            color: row?.isBillable === false ? '#adb5bd' : '#6c757d',
        }),
        value: (row) => {
            return `${row.index}. ${row.name}`
        },
    },
    {
        id: 'level',
        label: '',
        type: 'text',
        width: 40,
        style: (row) => ({
            opacity: row?.isBillable === false ? 0.45 : 1,
            fontStyle: 'italic',
            color: row?.isBillable === false ? '#adb5bd' : '#6c757d',
        }),
        value: (row) => {
            return row.billingLevel
        },
        component: ({ value, stores }) => {
            const row = stores.row.rowObject
            const staffId = findStaffIdByName(row.name)

            return (
                <div
                    style={{
                        display: 'flex',
                        width: '100%',
                        textAlign: 'right',
                        opacity: row?.isBillable === false ? 0.45 : 1,
                    }}
                >
                    <div
                        style={{
                            flex: '1 1 auto',
                            padding: '0 1em',
                            color:
                                row?.isBillable === false
                                    ? '#adb5bd'
                                    : '#6c757d',
                            fontStyle: 'italic',
                        }}
                    >
                        {value}
                    </div>
                    {staffId ? (
                        <Link
                            to="/staff/$id"
                            params={{ id: staffId }}
                            search={{ tab: 'permissions' }}
                            style={{
                                borderLeft: 'solid 1px #eee',
                                padding: '0 1em',
                                flex: '0 0 auto',
                                textDecoration: 'none',
                                color:
                                    row?.isBillable === false
                                        ? '#adb5bd'
                                        : '#6c757d',
                                fontStyle: 'italic',
                            }}
                        >
                            edit...
                        </Link>
                    ) : (
                        <div
                            style={{
                                borderLeft: 'solid 1px #eee',
                                padding: '0 1em',
                                flex: '0 0 auto',
                                color: '#ccc',
                                cursor: 'not-allowed',
                                fontStyle: 'italic',
                            }}
                        >
                            edit...
                        </div>
                    )}
                </div>
            )
        },
    },
    {
        id: 'fee',
        label: '',
        type: 'currency',
        width: 10,
        format: (v) => FormatCurrency(v),
        style: (row) => ({
            opacity: row?.isBillable === false ? 0.45 : 1,
            fontStyle: 'italic',
            color: row?.isBillable === false ? '#adb5bd' : '#6c757d',
        }),
        value: (row) => {
            return row.fee
        },
        component: ({ value, formattedValue, stores }) => {
            return value !== null ? formattedValue : ''
        },
    },
]

// Preview Table Component
const BillingPreviewTable = observer(() => {
    const previewData = BillingPageStore.subscriptionPreview
    const hasSubscription = SessionStore.subscription?.hasSubscription
    const isMobileView = useResponsiveStyles()

    if (!previewData || !BillingPageStore.showPreview) {
        return null
    }

    // Show loading state if preview is being fetched
    if (BillingPageStore.isFetchingPreview) {
        return (
            <div
                style={{
                    border: '2px dashed #007bff',
                    borderRadius: '8px',
                    padding: isMobileView ? '1.5em' : '2em',
                    margin: isMobileView ? '1em 0' : '2em 0',
                    backgroundColor: '#f8f9fa',
                    textAlign: 'center',
                }}
            >
                <MiniLoadingSpinner />
                <div
                    style={{
                        color: '#6c757d',
                        fontStyle: 'italic',
                        fontSize: isMobileView ? '0.85em' : '1em',
                        marginTop: '0.5em',
                    }}
                >
                    {isMobileView
                        ? 'Calculating...'
                        : 'Calculating billing preview...'}
                </div>
            </div>
        )
    }

    const previewPaymentRows = [
        {
            label: 'Date',
            percent: previewData.nextPaymentDate
                ? FormatDate(new Date(previewData.nextPaymentDate))
                : null,
            amount: null,
        },
        {
            label: 'Amount Due',
            percent: '',
            amount: previewData.amountDue,
        },
        previewData.discount
            ? {
                  label: 'Discount',
                  percent:
                      previewData.amountDue > 0
                          ? FormatPercent(
                                previewData.discount / previewData.amountDue
                            )
                          : '',
                  amount: previewData.discount * -1,
              }
            : null,
        previewData.adjustments && Math.abs(previewData.adjustments) > 0.01
            ? {
                  label: 'Adjustments',
                  percent: '',
                  amount: previewData.adjustments,
              }
            : null,
        previewData.tax && previewData.tax !== 0
            ? {
                  label: 'Tax',
                  percent:
                      BillingPageStore.previewComputedGrandTotal !== 0 &&
                      previewData.amountDue +
                          (previewData.adjustments || 0) -
                          (previewData.discount || 0) !==
                          0
                          ? FormatPercent(
                                previewData.tax /
                                    (previewData.amountDue +
                                        (previewData.adjustments || 0) -
                                        (previewData.discount || 0))
                            )
                          : '',
                  amount: previewData.tax,
              }
            : null,
        // Show credit if available (from preview data which includes actual customer credit balance)
        previewData.credit && previewData.credit !== 0
            ? {
                  label: 'Credit',
                  percent: '',
                  amount: -Math.abs(previewData.credit),
              }
            : null,
        {
            label: 'Total',
            percent: '',
            amount: BillingPageStore.previewComputedGrandTotal,
            // Special handling for credit scenarios
            isTotal: true,
            creditApplied:
                previewData.credit &&
                BillingPageStore.previewComputedGrandTotal <= 0
                    ? (() => {
                          // Calculate how much credit is actually being applied
                          const subtotal =
                              previewData.amountDue +
                              (previewData.tax || 0) +
                              (previewData.adjustments || 0) -
                              (previewData.discount || 0)
                          const availableCredit = Math.abs(previewData.credit)
                          return Math.min(availableCredit, subtotal)
                      })()
                    : null,
            remainingCredit: previewData.remainingCredit || 0,
        },
    ].filter((r) => r)

    const previewStaffRows = (previewData.staffBreakdown || [])
        .slice()
        .sort((a, b) => {
            // Sort by billing level first (admin, standard, none)
            const levelOrder = { admin: 0, standard: 1, none: 2 }
            const levelDiff =
                levelOrder[a.billingLevel] - levelOrder[b.billingLevel]
            if (levelDiff !== 0) return levelDiff

            // Then sort alphabetically by name within each level
            return a.name.localeCompare(b.name)
        })
        .map((s, i) => ({
            ...s,
            index: i + 1,
            // Ensure staff property is available for edit button
            staff: s.staff || findStaffByName(s.name),
        }))

    // Generate appropriate title based on scenario
    const getPreviewTitle = () => {
        const basePeriod = BillingPageStore.billingPeriod || 'monthly'

        if (!hasSubscription) {
            return isMobileView
                ? `💰 ${basePeriod} preview`
                : `💰 Preview: ${basePeriod} subscription`
        } else {
            const currentPeriod = SessionStore.subscription.subscriptionPeriod
            const newPeriod = BillingPageStore.billingPeriod
            if (newPeriod === currentPeriod) {
                return isMobileView
                    ? `👁️ Current billing`
                    : `👁️ Current: ${currentPeriod} billing`
            } else {
                const isUpgrade = newPeriod === 'yearly'
                return isMobileView
                    ? `${isUpgrade ? '📈' : '📉'} Switch to ${newPeriod}`
                    : `${isUpgrade ? '📈' : '📉'} Preview: switching to ${newPeriod} billing`
            }
        }
    }

    return (
        <div
            style={{
                opacity: 0.85,
                border: '2px dashed #007bff',
                borderRadius: '8px',
                padding: isMobileView ? '0.75em' : '1em',
                margin: isMobileView ? '1em 0' : '2em 0',
                backgroundColor: '#f8f9fa',
                overflow: 'hidden',
            }}
        >
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '1em',
                    color: '#007bff',
                    fontWeight: '600',
                    fontSize: isMobileView ? '0.9em' : '1em',
                }}
            >
                {getPreviewTitle()}
            </div>

            {!hasSubscription && (
                <div
                    style={{
                        fontSize: isMobileView ? '0.8em' : '0.85em',
                        color: '#6c757d',
                        fontStyle: 'italic',
                        marginBottom: '1em',
                        padding: isMobileView ? '0.5em' : '0.5em',
                        backgroundColor: '#ffffff',
                        borderRadius: '4px',
                        border: '1px solid #e9ecef',
                        lineHeight: '1.4',
                    }}
                >
                    💡{' '}
                    {isMobileView
                        ? "What you'll pay when subscribing"
                        : "This shows what you'll be charged when you subscribe."}
                    {BillingPageStore.billingPeriod === 'yearly' &&
                        (isMobileView
                            ? ' 15% savings!'
                            : ' Save 15% with yearly billing!')}
                </div>
            )}

            {hasSubscription &&
                BillingPageStore.billingPeriod !==
                    SessionStore.subscription.subscriptionPeriod && (
                    <div>
                        <div
                            style={{
                                fontSize: isMobileView ? '0.8em' : '0.85em',
                                color: '#6c757d',
                                fontStyle: 'italic',
                                marginBottom: '1em',
                                padding: isMobileView ? '0.5em' : '0.5em',
                                backgroundColor: '#ffffff',
                                borderRadius: '4px',
                                border: '1px solid #e9ecef',
                                lineHeight: '1.4',
                            }}
                        >
                            ⚡{' '}
                            {isMobileView
                                ? 'Includes prorations from current cycle'
                                : 'This preview includes prorations and adjustments from your current billing cycle.'}
                        </div>
                    </div>
                )}

            <div
                style={{
                    overflowX: isMobileView ? 'auto' : 'visible',
                    marginLeft: isMobileView ? '-0.75em' : '0',
                    marginRight: isMobileView ? '-0.75em' : '0',
                    paddingLeft: isMobileView ? '0.75em' : '0',
                    paddingRight: isMobileView ? '0.75em' : '0',
                }}
            >
                <Table
                    style={{
                        margin: '1em 0',
                        minWidth: isMobileView ? '300px' : 'auto',
                        fontSize: isMobileView ? '0.85em' : '1em',
                    }}
                    columns={previewPaymentColumns}
                    rows={previewPaymentRows}
                />

                {previewStaffRows.length > 0 && (
                    <Table
                        style={{
                            margin: '1em 0',
                            minWidth: isMobileView ? '300px' : 'auto',
                            fontSize: isMobileView ? '0.85em' : '1em',
                        }}
                        columns={createPreviewStaffColumns()}
                        rows={previewStaffRows}
                    />
                )}
            </div>
        </div>
    )
})

// Fresh Receipt Email Component
const FreshReceiptEmail = observer(() => {
    const receiptEmail = BillingPageStore.receiptEmail

    return (
        <div>
            {receiptEmail || (
                <span style={{ color: '#6c757d', fontStyle: 'italic' }}>
                    No receipt email set
                </span>
            )}
        </div>
    )
})

// Fresh Credit Balance Component
const FreshCreditBalance = observer(() => {
    const creditBalance = BillingPageStore.creditBalance

    return (
        <div>
            {FormatCurrency(creditBalance || 0, {
                decimals: 2,
            })}
        </div>
    )
})

// Payment Method Row Component with fresh Stripe data
const PaymentMethodRow = observer(() => {
    const paymentMethods = BillingPageStore.paymentMethods

    const formatPaymentMethod = (method) => {
        const methodType = method.type.replace(/_/g, ' ').toUpperCase()

        if (method.type === 'card') {
            return {
                type: method.cardBrand?.toUpperCase() || 'CARD',
                last4: method.cardLast4,
                expiry:
                    method.cardExpMonth && method.cardExpYear
                        ? `${method.cardExpMonth.toString().padStart(2, '0')}/${method.cardExpYear}`
                        : null,
            }
        } else if (method.type === 'au_becs_debit') {
            return {
                type: 'BECS DEBIT',
                last4: method.becsLast4,
                expiry: null,
            }
        } else if (method.type === 'us_bank_account') {
            return {
                type: `${method.bankAccountType?.toUpperCase() || 'BANK'} ACCOUNT`,
                last4: method.bankLast4,
                expiry: method.bankRoutingNumber
                    ? `Routing: ...${method.bankRoutingNumber.slice(-4)}`
                    : null,
            }
        } else {
            return {
                type: methodType,
                last4: null,
                expiry: null,
            }
        }
    }

    if (!paymentMethods?.length) {
        return (
            <div style={{ color: '#6c757d', fontStyle: 'italic' }}>
                No payment methods found
            </div>
        )
    }

    // Find the default payment method
    const defaultMethod =
        paymentMethods.find((pm) => pm.isDefault) || paymentMethods[0]
    const formatted = formatPaymentMethod(defaultMethod)

    return (
        <div
            style={{
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%',
                alignItems: 'center',
            }}
        >
            <div style={{ fontWeight: '500' }}>{formatted.type}</div>
            {formatted.last4 && (
                <div
                    style={{
                        borderLeft: 'solid 1px #eee',
                        padding: '0 1em',
                    }}
                >
                    ...{formatted.last4}
                </div>
            )}
            {formatted.expiry && (
                <div
                    style={{
                        borderLeft: 'solid 1px #eee',
                        padding: '0 1em',
                    }}
                >
                    {formatted.expiry}
                </div>
            )}
            {paymentMethods.length > 1 && (
                <div
                    style={{
                        borderLeft: 'solid 1px #eee',
                        padding: '0 1em',
                        fontSize: '0.85em',
                        color: '#6c757d',
                    }}
                >
                    +{paymentMethods.length - 1} more
                </div>
            )}
        </div>
    )
})

// Billing Portal Button Component
const BillingPortalButton = observer(() => {
    const [isCreatingPortalSession, setIsCreatingPortalSession] =
        React.useState(false)
    const isMobileView = useResponsiveStyles()

    const handleBillingPortalClick = async () => {
        try {
            setIsCreatingPortalSession(true)

            // Track billing portal session creation attempt
            posthog.capture('billing_portal_session_creation_started', {
                organisationId: SessionStore.organisation?.id,
            })

            // Create billing portal session using tRPC
            const result = await trpc.legacy.createBillingPortalSession.mutate(
                {}
            )

            if (result.success && result.sessionUrl) {
                // Track successful session creation
                posthog.capture('billing_portal_session_created', {
                    sessionId: result.sessionId,
                    organisationId: SessionStore.organisation?.id,
                })

                // Redirect to Stripe Billing Portal
                window.location.href = result.sessionUrl
            } else {
                throw new Error('Failed to create billing portal session')
            }
        } catch (error) {
            console.error('Error creating billing portal session:', error)

            // Track billing portal session creation failure
            posthog.capture('billing_portal_session_creation_failed', {
                error: error.message,
                organisationId: SessionStore.organisation?.id,
            })

            Sentry.captureException(error)
            posthog.captureException(error)

            // Show user-friendly error message
            alert('Failed to open billing portal. Please try again.')

            setIsCreatingPortalSession(false)
        }
    }

    return (
        <div
            style={{
                margin: isMobileView ? '1.5em 0' : '2em 0',
                padding: isMobileView ? '1em' : '1.5em',
                backgroundColor: '#f8f9fa',
                border: '1px solid #e9ecef',
                borderRadius: '8px',
                textAlign: 'center',
            }}
        >
            <div
                style={{
                    marginBottom: '1em',
                    fontSize: isMobileView ? '0.9em' : '1em',
                    color: '#6c757d',
                }}
            >
                Need to manage your billing details, view invoices, or update
                payment methods?
            </div>
            <button
                style={{
                    padding: isMobileView ? '0.75em 1.5em' : '0.8em 2em',
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: isCreatingPortalSession ? 'not-allowed' : 'pointer',
                    fontSize: isMobileView ? '0.9em' : '1em',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: isCreatingPortalSession ? 0.7 : 1,
                    margin: '0 auto',
                    minWidth: isMobileView ? '200px' : '220px',
                    transition: 'all 0.2s ease',
                }}
                onClick={handleBillingPortalClick}
                disabled={isCreatingPortalSession}
                onMouseOver={(e) => {
                    if (!isCreatingPortalSession) {
                        e.target.style.backgroundColor = '#0056b3'
                    }
                }}
                onMouseOut={(e) => {
                    if (!isCreatingPortalSession) {
                        e.target.style.backgroundColor = '#007bff'
                    }
                }}
            >
                {isCreatingPortalSession ? (
                    <>
                        <MiniLoadingSpinner />
                        <span style={{ marginLeft: '0.5em' }}>
                            Opening Portal...
                        </span>
                    </>
                ) : (
                    <>
                        <CreditCard
                            size={18}
                            style={{ marginRight: '0.5em' }}
                        />
                        Open Billing Portal
                    </>
                )}
            </button>
        </div>
    )
})

export default observer(() => {
    if (!SessionStore.subscription) {
        return null
    }
    return (
        <div className="mx-auto">
            {BillingPageStore.isSubscribing ? (
                <div
                    style={{
                        height: '100%',
                        position: 'fixed',
                        left: 0,
                        width: '100%',
                        background: '#FFFFFFaa',
                        textAlign: 'center',
                        zIndex: 1050,
                        // paddingTop: '8rem',
                    }}
                >
                    <div
                        style={{
                            backgroundColor: 'white',
                            width: '30rem',
                            height: '20rem',
                            display: 'inline-flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '1rem',
                            boxShadow: '0 0 2rem #00000033',
                        }}
                    >
                        <LoadingSpinner />
                        <div
                            style={{
                                fontWeight: 'bold',
                                marginTop: '-1em',
                                zIndex: 100,
                            }}
                        >
                            Processing Subscription
                        </div>
                    </div>
                </div>
            ) : null}

            {/* Billing Portal Button - only show for active subscriptions */}
            {SessionStore.subscription.hasSubscription && (
                <BillingPortalButton />
            )}

            {/* Billing Period Toggle */}
            <BillingPeriodToggle />

            {/* Preview Table */}
            <BillingPreviewTable />

            {SessionStore.subscription.hasSubscription ? (
                <Table
                    style={{ margin: '3em 0' }}
                    columns={paymentDetailColumns}
                    rows={[
                        {
                            label: 'Organisation',
                            value: SessionStore.organisation.name,
                        },
                        {
                            label: 'Card',
                            value: <PaymentMethodRow />,
                        },
                        {
                            label: 'Receipt Email',
                            value: <FreshReceiptEmail />,
                        },
                        {
                            label: 'Credit',
                            value: <FreshCreditBalance />,
                        },
                    ]}
                />
            ) : null}

            {SessionStore.subscription.unpaidInvoices?.length ? (
                <Table
                    style={{ margin: '3em 0' }}
                    columns={unpaidInvoiceColumns}
                    rows={SessionStore.subscription.unpaidInvoices}
                />
            ) : null}

            {/* Hide current tables when in preview mode */}
            {!BillingPageStore.shouldShowPreview && (
                <>
                    <Table
                        style={{ margin: '3em 0' }}
                        columns={nextPaymentColumns}
                        rows={[
                            {
                                label: 'Date',
                                percent: BillingPageStore.nextPaymentDate
                                    ? FormatDate(
                                          new Date(
                                              BillingPageStore.nextPaymentDate
                                          )
                                      )
                                    : null,
                                amount: null,
                            },
                            {
                                label: 'Amount Due',
                                percent: '',
                                amount: BillingPageStore.amountDue,
                            },
                            BillingPageStore.discount
                                ? {
                                      label: 'Discount',
                                      percent:
                                          BillingPageStore.amountDue > 0
                                              ? FormatPercent(
                                                    BillingPageStore.discount /
                                                        BillingPageStore.amountDue
                                                )
                                              : '',
                                      amount: BillingPageStore.discount * -1,
                                  }
                                : null,
                            BillingPageStore.adjustments &&
                            Math.abs(BillingPageStore.adjustments) > 0.01
                                ? {
                                      label: 'Adjustments',
                                      percent: '',
                                      amount: BillingPageStore.adjustments,
                                  }
                                : null,
                            BillingPageStore.tax && BillingPageStore.tax !== 0
                                ? {
                                      label: 'Tax',
                                      percent:
                                          BillingPageStore.computedGrandTotal !==
                                              0 &&
                                          BillingPageStore.amountDue +
                                              (BillingPageStore.adjustments ||
                                                  0) -
                                              (BillingPageStore.discount ||
                                                  0) !==
                                              0
                                              ? FormatPercent(
                                                    BillingPageStore.tax /
                                                        (BillingPageStore.amountDue +
                                                            (BillingPageStore.adjustments ||
                                                                0) -
                                                            (BillingPageStore.discount ||
                                                                0))
                                                )
                                              : '',
                                      amount: BillingPageStore.tax,
                                  }
                                : null,
                            BillingPageStore.credit &&
                            BillingPageStore.credit !== 0
                                ? {
                                      label: 'Credit',
                                      percent: '',
                                      amount: -Math.abs(
                                          BillingPageStore.credit
                                      ),
                                  }
                                : null,
                            {
                                label: 'Total',
                                percent: '',
                                amount: BillingPageStore.computedGrandTotal,
                                // Special handling for credit scenarios
                                isTotal: true,
                                creditApplied:
                                    BillingPageStore.credit &&
                                    BillingPageStore.computedGrandTotal === 0
                                        ? (() => {
                                              // Calculate how much credit is actually being applied
                                              const subtotal =
                                                  BillingPageStore.amountDue +
                                                  (BillingPageStore.tax || 0) +
                                                  (BillingPageStore.adjustments ||
                                                      0) -
                                                  (BillingPageStore.discount ||
                                                      0)
                                              const availableCredit = Math.abs(
                                                  BillingPageStore.credit
                                              )
                                              return Math.min(
                                                  availableCredit,
                                                  subtotal
                                              )
                                          })()
                                        : null,
                                // Use server-provided remainingCredit value
                                remainingCredit:
                                    BillingPageStore.remainingCredit || 0,
                            },
                        ].filter((r) => r)}
                    />
                    <Table
                        style={{ margin: '3em 0' }}
                        columns={createStaffColumns()}
                        rows={BillingPageStore.computedStaffBreakdown
                            .slice()
                            .sort((a, b) => {
                                // Sort by billing level first (admin, standard, none)
                                const levelOrder = {
                                    admin: 0,
                                    standard: 1,
                                    none: 2,
                                }
                                const levelDiff =
                                    levelOrder[a.billingLevel] -
                                    levelOrder[b.billingLevel]
                                if (levelDiff !== 0) return levelDiff

                                // Then sort alphabetically by name within each level
                                return a.name.localeCompare(b.name)
                            })
                            .map((s, i) => ({
                                ...s,
                                index: i + 1,
                                fullName: s.name,
                                permissionLevel: s.billingLevel,
                                // Add staff object for edit functionality
                                staff: findStaffByName(s.name),
                            }))}
                    />
                </>
            )}
        </div>
    )
})
